import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Chart } from 'react-chartjs-2';

import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import Table from '../../components/Table';

import { API_URL, ENDPOINTS } from '../../constants/api';
import { apiKey, fetchForm } from '../../utils/ktcUtils';
import { CURRENT_DATE } from '../../constants';

ChartJS.register(
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title
);

/* ==================================================================== */
/* 1. Small helpers                                                     */
/* ==================================================================== */

const ALL = 'All';
const PAGE_SIZES = [25, 50, 100, 250];
const MAX_CATEGORIES = 10;
const PRIMARY_RGB = [13, 110, 253];

const BASE_COLORS = [
  '#4CAF50',
  '#2196F3',
  '#FF9800',
  '#F44336',
  '#9C27B0',
  '#009688',
  '#795548',
  '#607D8B',
];

const colorAt = (i) =>
  i < BASE_COLORS.length ? BASE_COLORS[i] : `hsl(${(i * 47) % 360}, 60%, 50%)`;

const isEmpty = (v) => v === undefined || v === null || v === '';

const toNumber = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (n) =>
  Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getColumns = (objects) => {
  const set = new Set();
  objects.forEach((obj) => Object.keys(obj).forEach((key) => set.add(key)));
  return [...set];
};

const slug = (text) =>
  String(text || 'report')
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, '_')
    .replace(/^_+|_+$/g, '') || 'report';

const makeFileName = (report, ext, suffix = '') =>
  `${slug(report.title)}${suffix}_${report.startDate}_${report.endDate}.${ext}`;

// Local-time YYYY-MM-DD (toISOString would shift the day in some timezones)
const toISODate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

const DATE_PRESETS = [
  {
    label: 'Today',
    range: () => {
      const t = toISODate(new Date());
      return [t, t];
    },
  },
  {
    label: 'Last 7 days',
    range: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      return [toISODate(start), toISODate(end)];
    },
  },
  {
    label: 'Last 30 days',
    range: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 29);
      return [toISODate(start), toISODate(end)];
    },
  },
  {
    label: 'This month',
    range: () => {
      const now = new Date();
      return [
        toISODate(new Date(now.getFullYear(), now.getMonth(), 1)),
        toISODate(now),
      ];
    },
  },
];

// Numbers first (so 2 < 10), then text with natural ordering
const compareValues = (a, b) => {
  const sa = String(a ?? '').trim();
  const sb = String(b ?? '').trim();
  const numeric = /^-?[\d,]*\.?\d+$/;

  if (numeric.test(sa) && numeric.test(sb)) {
    return toNumber(sa) - toNumber(sb);
  }
  return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
};

/* ==================================================================== */
/* 2. Cell helpers                                                      */
/*                                                                      */
/* The Table component renders HTML links/buttons, images and file      */
/* paths inside cells. Tabs, search, sorting, charts, cards and every   */
/* export work on the readable version of those cells instead.          */
/* ==================================================================== */

const IMAGE_EXT = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.jfif'];
const DOC_EXT = [
  '.pdf', '.doc', '.docx', '.xlsx', '.xls', '.ppt', '.pptx',
  '.csv', '.txt', '.mp4', '.mov', '.wmv', '.avi',
];

const ANCHOR_HREF = /<a\b[^>]*?\bhref\s*=\s*["']([^"']+)["']/i;
const HAS_TAG = /<\/?[a-z][^>]*>/i;

const endsWithAny = (text, list) => {
  const lower = text.toLowerCase();
  return list.some((ext) => lower.endsWith(ext));
};

// DOMParser builds an inert document: nothing loads and no scripts run
const plainText = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const toFileUrl = (path) =>
  path.includes('http') ? path : `${API_URL}/${encodeURI(path)}`;

const toAbsoluteUrl = (href) => {
  try {
    return new URL(href, window.location.origin).href;
  } catch (err) {
    return href;
  }
};

/** Returns { text, url? } - the readable value of any cell. */
const extractCell = (value) => {
  if (value === null || value === undefined) return { text: '' };
  if (typeof value === 'object') return { text: JSON.stringify(value) };
  if (typeof value !== 'string') return { text: value };

  const raw = value.trim();
  if (!raw) return { text: '' };

  // Link / button cells: keep the label, remember the link
  const anchor = raw.match(ANCHOR_HREF);
  if (anchor) {
    const href = anchor[1];
    return {
      text: plainText(raw),
      url: href.startsWith('form-modal?') ? undefined : toAbsoluteUrl(href),
    };
  }

  // One or more images
  if (endsWithAny(raw, IMAGE_EXT)) {
    const urls = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(toFileUrl);
    return { text: urls.join(', '), url: urls[0] };
  }

  // Documents and videos
  if (endsWithAny(raw, DOC_EXT)) {
    const url = toFileUrl(raw);
    return { text: url, url };
  }

  // Any other HTML
  if (HAS_TAG.test(raw)) return { text: plainText(raw) };

  return { text: value };
};

const toPlainRow = (raw) =>
  Object.fromEntries(
    Object.entries(raw).map(([key, value]) => [key, extractCell(value).text])
  );

// Excel: store amounts and quantities as real numbers. Values with leading
// zeros (codes) or very long digit strings (phone numbers) stay as text.
const toExcelValue = (value) => {
  if (typeof value !== 'string') return value;
  const s = value.trim();
  const plain = /^-?(0|[1-9]\d{0,8})(\.\d{1,6})?$/.test(s);
  const grouped = /^-?[1-9]\d{0,2}(,\d{3})+(\.\d{1,6})?$/.test(s);
  return plain || grouped ? Number(s.replace(/,/g, '')) : value;
};

// Columns that only hold action buttons (a "Print" column, Edit, Delete ...)
// are useless on paper, so Print and PDF leave them out.
const ACTION_NAME = /^\s*(print|actions?)\s*$/i;
const BUTTON_ANCHOR = /<a\b[^>]*\bclass(?:Name)?\s*=/i;

const findActionColumns = (records, columns) => {
  const found = new Set();

  columns.forEach((col) => {
    if (ACTION_NAME.test(col)) {
      found.add(col);
      return;
    }

    let hasValue = false;
    const onlyButtons = records.every(({ raw }) => {
      const v = raw[col];
      if (isEmpty(v)) return true;
      hasValue = true;
      return typeof v === 'string' && BUTTON_ANCHOR.test(v);
    });

    if (hasValue && onlyButtons) found.add(col);
  });

  return found;
};

/* ==================================================================== */
/* 3. Charts                                                            */
/* ==================================================================== */

const CHART_TYPES = ['pie', 'doughnut', 'bar', 'hbar', 'line'];

const normalizeChartType = (type) =>
  CHART_TYPES.includes(type) ? type : 'pie';

/** Rows per distinct value, high to low. The tail is merged into "Others". */
const countBy = (plainRows, field, maxItems = MAX_CATEGORIES) => {
  const counts = new Map();

  plainRows.forEach((row) => {
    const value = row[field];
    const key = isEmpty(value) ? 'Unknown' : String(value);
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  let entries = [...counts.entries()].sort((a, b) => b[1] - a[1]);

  if (entries.length > maxItems) {
    const rest = entries.slice(maxItems - 1);
    entries = [
      ...entries.slice(0, maxItems - 1),
      ['Others', rest.reduce((sum, [, count]) => sum + count, 0)],
    ];
  }

  return entries;
};

const whiteBackground = {
  id: 'whiteBackground',
  beforeDraw(chart) {
    const { ctx, width, height } = chart;
    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  },
};

/**
 * Chart.js config for one field. The type comes from the column key,
 * e.g. "status~charts:bar". Supported: pie (default), doughnut, bar, hbar, line.
 */
const buildChartConfig = (
  plainRows,
  field,
  chartType = 'pie',
  { forExport = false } = {}
) => {
  const type = normalizeChartType(chartType);
  const entries = countBy(plainRows, field);
  const labels = entries.map(([label]) => label);
  const data = entries.map(([, count]) => count);
  const total = data.reduce((sum, n) => sum + n, 0);

  const circular = type === 'pie' || type === 'doughnut';
  const horizontal = type === 'hbar';
  const valueAxis = horizontal ? 'x' : 'y';
  const colors = labels.map((_, i) => colorAt(i));

  let dataset;
  if (circular) {
    dataset = {
      label: field,
      data,
      backgroundColor: colors,
      borderColor: '#ffffff',
      borderWidth: 1,
    };
  } else if (type === 'line') {
    dataset = {
      label: field,
      data,
      borderColor: BASE_COLORS[1],
      backgroundColor: 'rgba(33, 150, 243, 0.15)',
      fill: true,
      tension: 0.3,
      pointRadius: 4,
    };
  } else {
    dataset = { label: field, data, backgroundColor: colors, borderRadius: 4 };
  }

  const options = {
    ...(horizontal ? { indexAxis: 'y' } : {}),
    plugins: {
      legend: {
        display: circular,
        position: forExport ? 'right' : 'bottom',
        ...(forExport ? { labels: { font: { size: 13 }, boxWidth: 14 } } : {}),
      },
      title: {
        display: forExport,
        text: field,
        font: { size: 18 },
        padding: { bottom: 12 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const value =
              typeof ctx.parsed === 'number' ? ctx.parsed : ctx.parsed[valueAxis];
            const pct = total ? ((value / total) * 100).toFixed(1) : '0.0';
            return ` ${ctx.label}: ${value} (${pct}%)`;
          },
        },
      },
    },
    ...(circular
      ? {}
      : { scales: { [valueAxis]: { beginAtZero: true, ticks: { precision: 0 } } } }),
    ...(forExport
      ? { responsive: false, animation: false, devicePixelRatio: 3 }
      : {}),
  };

  return {
    type: horizontal ? 'bar' : type,
    data: { labels, datasets: [dataset] },
    options,
    plugins: forExport ? [whiteBackground] : [],
  };
};

/**
 * Draws a chart on a detached canvas at a fixed size and returns a copy of it
 * (Chart.js clears its canvas on destroy). PDF and PNG exports use this, so
 * they stay sharp whatever the on-screen card width is.
 */
const renderChartCanvas = (plainRows, field, chartType) => {
  const type = normalizeChartType(chartType);

  const canvas = document.createElement('canvas');
  canvas.width = 600;
  canvas.height = 340;

  const chart = new ChartJS(
    canvas,
    buildChartConfig(plainRows, field, type, { forExport: true })
  );

  const copy = document.createElement('canvas');
  copy.width = canvas.width;
  copy.height = canvas.height;
  copy.getContext('2d').drawImage(canvas, 0, 0);

  chart.destroy();
  return copy;
};

/* ==================================================================== */
/* 4. Exporters                                                         */
/*                                                                      */
/* `report` = {                                                         */
/*   title, startDate, endDate,                                         */
/*   rows,       // raw rows (cells may contain HTML)                   */
/*   plainRows,  // same rows as readable text (for charts)            */
/*   columns,    // visible column keys                                 */
/*   filters,    // [{ field, value }]                                  */
/*   cards,      // [{ field, total }]                                  */
/*   charts,     // [{ field, chartType }]                              */
/* }                                                                    */
/* ==================================================================== */

const describeFilters = (filters) =>
  filters.map(({ field, value }) => `${field} = ${value}`).join('; ');

const buildCells = (rows, columns) =>
  rows.map((row) => columns.map((col) => extractCell(row[col])));

const columnWidth = (header, cells, colIndex) => {
  const longest = cells
    .slice(0, 200)
    .reduce(
      (max, line) => Math.max(max, String(line[colIndex].text).length),
      String(header).length
    );
  return Math.min(45, longest + 2);
};

/** Excel: Data (with live links), Summary and Charts Data sheets. */
const exportExcel = (report) => {
  const {
    rows,
    plainRows,
    columns,
    title,
    startDate,
    endDate,
    filters,
    cards,
    charts,
  } = report;
  const wb = XLSX.utils.book_new();

  // 1. Data
  const cells = buildCells(rows, columns);
  const aoa = [
    columns,
    ...cells.map((line) => line.map((cell) => toExcelValue(cell.text))),
  ];
  const wsData = XLSX.utils.aoa_to_sheet(aoa);

  cells.forEach((line, r) =>
    line.forEach((cell, c) => {
      if (!cell.url) return;
      const target = wsData[XLSX.utils.encode_cell({ r: r + 1, c })];
      if (target) target.l = { Target: cell.url };
    })
  );

  wsData['!cols'] = columns.map((col, c) => ({
    wch: columnWidth(col, cells, c),
  }));
  if (wsData['!ref']) wsData['!autofilter'] = { ref: wsData['!ref'] };
  XLSX.utils.book_append_sheet(wb, wsData, 'Data');

  // 2. Summary
  const summary = [
    [title],
    [],
    ['From', startDate],
    ['To', endDate],
    ['Rows', rows.length],
    ['Generated', new Date().toLocaleString()],
  ];
  if (filters.length) summary.push(['Filters', describeFilters(filters)]);
  if (cards.length) {
    summary.push([], ['Totals'], ['Metric', 'Total']);
    cards.forEach(({ field, total }) => summary.push([field, total]));
  }
  const wsSummary = XLSX.utils.aoa_to_sheet(summary);
  wsSummary['!cols'] = [{ wch: 24 }, { wch: 36 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  // 3. Chart data (the counts behind every chart)
  if (charts.length) {
    const chartAoa = [];
    const percentRows = [];

    charts.forEach(({ field }) => {
      const entries = countBy(plainRows, field, Infinity);
      const total = entries.reduce((sum, [, count]) => sum + count, 0);

      chartAoa.push([field, 'Count', 'Percent']);
      entries.forEach(([label, count]) => {
        percentRows.push(chartAoa.length);
        chartAoa.push([label, count, total ? count / total : 0]);
      });
      chartAoa.push([]);
    });

    const wsCharts = XLSX.utils.aoa_to_sheet(chartAoa);
    percentRows.forEach((r) => {
      const cell = wsCharts[XLSX.utils.encode_cell({ r, c: 2 })];
      if (cell) cell.z = '0.0%';
    });
    wsCharts['!cols'] = [{ wch: 30 }, { wch: 10 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, wsCharts, 'Charts Data');
  }

  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([buffer], { type: 'application/octet-stream' }),
    makeFileName(report, 'xlsx')
  );
};

/** CSV with a UTF-8 BOM so Excel shows non-English text correctly. */
const exportCSV = (report) => {
  const { rows, columns } = report;
  const aoa = [
    columns,
    ...buildCells(rows, columns).map((line) => line.map((cell) => cell.text)),
  ];
  const csv = XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet(aoa));

  saveAs(
    new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' }),
    makeFileName(report, 'csv')
  );
};

/** One chart as a PNG file. */
const exportChartPNG = (report, { field, chartType }) => {
  renderChartCanvas(report.plainRows, field, chartType).toBlob((blob) => {
    if (blob) saveAs(blob, makeFileName(report, 'png', `_${slug(field)}`));
  });
};

/** All charts stitched into one PNG (two per row). */
const exportChartsPNG = (report) => {
  const { plainRows, charts } = report;
  if (!charts.length) return;

  const canvases = charts.map(({ field, chartType }) =>
    renderChartCanvas(plainRows, field, chartType)
  );

  const cols = canvases.length > 1 ? 2 : 1;
  const cellW = 1200;
  const gap = 24;
  const heights = canvases.map((c) => Math.round((c.height * cellW) / c.width));

  const rowHeights = [];
  for (let i = 0; i < canvases.length; i += cols) {
    rowHeights.push(Math.max(...heights.slice(i, i + cols)));
  }

  const out = document.createElement('canvas');
  out.width = cols * cellW + (cols + 1) * gap;
  out.height =
    rowHeights.reduce((sum, h) => sum + h, 0) + (rowHeights.length + 1) * gap;

  const ctx = out.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);

  canvases.forEach((canvas, i) => {
    const rowIndex = Math.floor(i / cols);
    const colIndex = i % cols;
    const x = gap + colIndex * (cellW + gap);
    const y =
      gap + rowHeights.slice(0, rowIndex).reduce((sum, h) => sum + h + gap, 0);
    ctx.drawImage(canvas, x, y, cellW, heights[i]);
  });

  out.toBlob((blob) => {
    if (blob) saveAs(blob, makeFileName(report, 'png', '_charts'));
  });
};

/** PDF: title, filters, totals, charts (optional) and the table. */
const exportPDF = (report, { includeCharts = true } = {}) => {
  const {
    rows,
    plainRows,
    columns,
    title,
    startDate,
    endDate,
    filters,
    cards,
    charts,
  } = report;

  const doc = new jsPDF({
    orientation: columns.length > 6 ? 'landscape' : 'portrait',
    unit: 'pt',
    format: 'a4',
  });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 36;
  let y = M;

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(33);
  doc.text(String(title), M, y + 12);
  y += 28;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(
    `Period: ${startDate} to ${endDate}  |  Rows: ${rows.length}`,
    M,
    y
  );
  y += 12;

  if (filters.length) {
    const lines = doc.splitTextToSize(
      `Filters: ${describeFilters(filters)}`,
      pageW - M * 2
    );
    doc.text(lines, M, y);
    y += lines.length * 11;
  }
  y += 10;
  doc.setTextColor(0);

  // Totals
  if (cards.length) {
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Total']],
      body: cards.map(({ field, total }) => [field, formatNumber(total)]),
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: PRIMARY_RGB },
      columnStyles: { 1: { halign: 'right' } },
      tableWidth: 280,
      margin: { left: M, right: M },
    });
    y = (doc.lastAutoTable?.finalY ?? y) + 18;
  }

  // Charts
  if (includeCharts && charts.length) {
    const perRow = 2;
    const gap = 14;
    const maxH = 230;
    const cellW = (pageW - M * 2 - gap * (perRow - 1)) / perRow;

    const images = charts.map(({ field, chartType }) => {
      const canvas = renderChartCanvas(plainRows, field, chartType);
      return { canvas, ratio: canvas.width / canvas.height };
    });

    for (let i = 0; i < images.length; i += perRow) {
      const sized = images.slice(i, i + perRow).map((img) => {
        let w = cellW;
        let h = w / img.ratio;
        if (h > maxH) {
          h = maxH;
          w = h * img.ratio;
        }
        return { ...img, w, h };
      });

      const rowH = Math.max(...sized.map((s) => s.h));
      if (y + rowH > pageH - M) {
        doc.addPage();
        y = M;
      }

      sized.forEach((s, idx) => {
        const x = M + idx * (cellW + gap) + (cellW - s.w) / 2;
        doc.addImage(
          s.canvas.toDataURL('image/png'),
          'PNG',
          x,
          y,
          s.w,
          s.h,
          undefined,
          'FAST'
        );
      });

      y += rowH + gap;
    }
    y += 4;
  }

  // Table (links and file paths stay clickable)
  if (y > pageH - 120) {
    doc.addPage();
    y = M;
  }

  const cells = buildCells(rows, columns);
  const urls = cells.map((line) => line.map((cell) => cell.url));

  autoTable(doc, {
    startY: y,
    head: [columns],
    body: cells.map((line) => line.map((cell) => String(cell.text))),
    styles: {
      fontSize: columns.length > 12 ? 6.5 : 8,
      cellPadding: 3,
      overflow: 'linebreak',
    },
    headStyles: { fillColor: PRIMARY_RGB },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: M, right: M, bottom: 30 },
    showHead: 'everyPage',
    didDrawCell: (data) => {
      if (data.section !== 'body') return;
      const url = urls[data.row.index]?.[data.column.index];
      if (url) {
        doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
          url,
        });
      }
    },
  });

  // Footer on every page
  const pages = doc.internal.getNumberOfPages();
  const generated = `Generated ${new Date().toLocaleString()}`;
  for (let p = 1; p <= pages; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(generated, M, pageH - 16);
    doc.text(`Page ${p} of ${pages}`, pageW - M, pageH - 16, { align: 'right' });
  }

  doc.save(makeFileName(report, 'pdf', includeCharts ? '' : '_table'));
};

/* ==================================================================== */
/* 5. Report parsing                                                    */
/* ==================================================================== */

/**
 * Column keys can carry actions, e.g. "status~tabs~charts:bar~hide".
 * Hidden columns stay in the data (tabs, cards and charts can still use
 * them) and are only left out of the table and the exports.
 */
const parseReport = (data) => {
  const fields = {};
  const hidden = new Set();

  const records = (Array.isArray(data) ? data : []).map((row, i) => {
    const raw = {};

    Object.entries(row).forEach(([key, value]) => {
      const [realKey, ...actions] = key.split('~');
      raw[realKey] = value;

      if (!fields[realKey]) {
        fields[realKey] = {
          tabs: false,
          charts: false,
          cards: false,
          chartType: 'pie',
        };
      }

      actions.forEach((action) => {
        const [type, param] = action.split(':');

        switch (type) {
          case 'tabs':
            fields[realKey].tabs = true;
            break;
          case 'charts':
            fields[realKey].charts = true;
            fields[realKey].chartType = normalizeChartType(param);
            break;
          case 'cards':
            fields[realKey].cards = true;
            break;
          case 'hide':
            hidden.add(realKey);
            break;
          default:
            break;
        }
      });
    });

    return { i, raw, plain: toPlainRow(raw) };
  });

  return { records, config: { fields, hiddenColumns: [...hidden] } };
};

/* ==================================================================== */
/* 6. Page                                                              */
/* ==================================================================== */

// Draws an already-rendered chart canvas into the page. Used for printing only:
// it is drawn synchronously, so it is ready when the print preview opens.
function PrintChart({ canvas }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.width = canvas.width;
    el.height = canvas.height;
    el.getContext('2d').drawImage(canvas, 0, 0);
  }, [canvas]);

  return <canvas ref={ref} />;
}

export default function ChartsPage() {
  const { route } = useParams();

  const [records, setRecords] = useState([]); // [{ i, raw, plain }]
  const [config, setConfig] = useState({ fields: {}, hiddenColumns: [] });
  const [activeTabs, setActiveTabs] = useState({});
  const [activeField, setActiveField] = useState(null);
  const [link, setLink] = useState({});
  const [, setInputs] = useState([]);
  const [, setValues] = useState([]);
  const [, setLoading] = useState(true);
  const [, setFormReportPer] = useState([]);
  const [startDate, setStartDate] = useState(CURRENT_DATE);
  const [endDate, setEndDate] = useState(CURRENT_DATE);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [printAll, setPrintAll] = useState(false);
  const [userHidden, setUserHidden] = useState(() => new Set()); // columns unticked in the Columns menu
  const [colsOpen, setColsOpen] = useState(false);

  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const colsRef = useRef(null);
  const requestId = useRef(0);

  /* ---------------------------- data loading ---------------------------- */

  const handleSearch = useCallback(
    async (from = startDate, to = endDate) => {
      if (!route) return;

      if (from && to && from > to) {
        setError('The start date must be on or before the end date.');
        return;
      }

      const id = ++requestId.current;
      setSearching(true);
      setError('');

      try {
        const res = await axios.post(
          `${API_URL}/${ENDPOINTS.SYS}/reportcustom-apikey/${route}`,
          { from, to },
          { headers: { 'x-api-key': apiKey } }
        );

        // Ignore the response if a newer search started meanwhile
        if (id !== requestId.current) return;

        const parsed = parseReport(res.data?.data);
        setRecords(parsed.records);
        setConfig(parsed.config);
      } catch (err) {
        if (id !== requestId.current) return;
        console.error(err);
        setRecords([]);
        setError(
          err?.response?.data?.message ||
            'Could not load the report. Check your connection and try again.'
        );
      } finally {
        if (id === requestId.current) setSearching(false);
      }
    },
    [route, startDate, endDate]
  );

  // Load the form info + first report whenever the route changes
  useEffect(() => {
    if (!route) return;

    setActiveTabs({});
    setSearchInput('');
    setSearch('');
    setSort({ key: '', dir: 'asc' });
    setUserHidden(new Set());
    setRecords([]);
    setConfig({ fields: {}, hiddenColumns: [] });

    const queryObj = Object.fromEntries(
      new URLSearchParams(window.location.search).entries()
    );

    Promise.resolve(
      fetchForm({
        url: `${API_URL}/${ENDPOINTS.SYS}/createform-apikey/${route}`,
        queryObj,
        setLink,
        setInputs,
        setValues,
        setLoading,
        setFormReportPer,
        isApiKey: true,
      })
    ).catch((err) => console.error(err));

    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  // Debounce the table filter box
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Close the Export / Columns menus when clicking elsewhere
  useEffect(() => {
    if (!menuOpen && !colsOpen) return undefined;

    const onMouseDown = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (colsOpen && colsRef.current && !colsRef.current.contains(e.target)) {
        setColsOpen(false);
      }
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [menuOpen, colsOpen]);

  // Print (button or Ctrl+P) always includes every row, not just the page
  useEffect(() => {
    const before = () => flushSync(() => setPrintAll(true));
    const after = () => setPrintAll(false);

    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
    };
  }, []);

  /* ---------------------------- derived data ---------------------------- */

  const hiddenSet = useMemo(
    () => new Set(config.hiddenColumns),
    [config.hiddenColumns]
  );

  const allColumns = useMemo(
    () => getColumns(records.map((r) => r.raw)),
    [records]
  );

  // Columns the user can show/hide (the API's "~hide" columns never appear)
  const selectableColumns = useMemo(
    () => allColumns.filter((c) => !hiddenSet.has(c)),
    [allColumns, hiddenSet]
  );

  // What the table shows (all ticked by default)
  const visibleColumns = useMemo(
    () => selectableColumns.filter((c) => !userHidden.has(c)),
    [selectableColumns, userHidden]
  );

  const actionColumns = useMemo(
    () => findActionColumns(records, selectableColumns),
    [records, selectableColumns]
  );

  // What Print and PDF show: visible columns without action-button columns
  const printColumns = useMemo(
    () => visibleColumns.filter((c) => !actionColumns.has(c)),
    [visibleColumns, actionColumns]
  );

  const tabFields = useMemo(
    () =>
      Object.entries(config.fields)
        .filter(([, v]) => v.tabs)
        .map(([k]) => k),
    [config.fields]
  );

  const chartFields = useMemo(
    () =>
      Object.entries(config.fields)
        .filter(([, v]) => v.charts)
        .map(([field, v]) => ({ field, chartType: v.chartType })),
    [config.fields]
  );

  const cardFields = useMemo(
    () =>
      Object.entries(config.fields)
        .filter(([, v]) => v.cards)
        .map(([k]) => k),
    [config.fields]
  );

  // Keep a valid tab field selected (the first one by default)
  useEffect(() => {
    if (!tabFields.includes(activeField)) {
      setActiveField(tabFields[0] ?? null);
    }
  }, [tabFields, activeField]);

  // Drop the sort column if it is no longer in the table
  useEffect(() => {
    if (sort.key && !selectableColumns.includes(sort.key)) {
      setSort({ key: '', dir: 'asc' });
    }
  }, [selectableColumns, sort.key]);

  // One lowercase string per record, so the filter box stays fast
  const searchIndex = useMemo(
    () =>
      records.map((rec) =>
        visibleColumns
          .map((c) => (isEmpty(rec.plain[c]) ? '' : rec.plain[c]))
          .join(' ')
          .toLowerCase()
      ),
    [records, visibleColumns]
  );

  // Rows matching the filter box and every active tab (optionally ignoring one tab field)
  const applyFilters = useCallback(
    (exceptField) => {
      const q = search.trim().toLowerCase();

      return records.filter((rec) => {
        if (q && !searchIndex[rec.i].includes(q)) return false;

        return Object.entries(activeTabs).every(
          ([field, value]) =>
            field === exceptField ||
            !value ||
            value === ALL ||
            String(rec.plain[field] ?? '') === value
        );
      });
    },
    [records, searchIndex, search, activeTabs]
  );

  const filteredRecords = useMemo(() => {
    const list = applyFilters();
    if (!sort.key) return list;

    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...list].sort(
      (a, b) => dir * compareValues(a.plain[sort.key], b.plain[sort.key])
    );
  }, [applyFilters, sort]);

  const filteredPlain = useMemo(
    () => filteredRecords.map((r) => r.plain),
    [filteredRecords]
  );

  const activeFilters = useMemo(
    () =>
      Object.entries(activeTabs)
        .filter(([, value]) => value && value !== ALL)
        .map(([field, value]) => ({ field, value })),
    [activeTabs]
  );

  const trimmedSearch = search.trim();

  // Everything that narrows the report, shown as removable chips and in exports
  const filterChips = useMemo(
    () => [
      ...activeFilters.map(({ field, value }) => ({
        key: field,
        label: field,
        value,
        remove: () => setActiveTabs((prev) => ({ ...prev, [field]: ALL })),
      })),
      ...(trimmedSearch
        ? [
            {
              key: '__search',
              label: 'Search',
              value: trimmedSearch,
              remove: () => {
                setSearchInput('');
                setSearch('');
              },
            },
          ]
        : []),
    ],
    [activeFilters, trimmedSearch]
  );

  // How many distinct values each tab field has
  const distinctCounts = useMemo(
    () =>
      Object.fromEntries(
        tabFields.map((field) => [
          field,
          new Set(
            records
              .map((r) => r.plain[field])
              .filter((v) => !isEmpty(v))
              .map(String)
          ).size,
        ])
      ),
    [tabFields, records]
  );

  // Buttons for the selected tab field. Counts respect the OTHER filters.
  const tabOptions = useMemo(() => {
    if (!activeField) return [];

    const base = applyFilters(activeField);
    const counts = new Map();

    base.forEach((rec) => {
      const v = rec.plain[activeField];
      if (isEmpty(v)) return;
      counts.set(String(v), (counts.get(String(v)) || 0) + 1);
    });

    return [
      { value: ALL, count: base.length },
      ...[...counts.entries()].map(([value, count]) => ({ value, count })),
    ];
  }, [activeField, applyFilters]);

  const cardTotals = useMemo(
    () =>
      cardFields.map((field) => ({
        field,
        total: filteredPlain.reduce((sum, r) => sum + toNumber(r[field]), 0),
      })),
    [cardFields, filteredPlain]
  );

  const charts = useMemo(
    () =>
      chartFields.map(({ field, chartType }) => ({
        field,
        chartType,
        cfg: buildChartConfig(filteredPlain, field, chartType),
      })),
    [chartFields, filteredPlain]
  );

  // Pagination (printing shows every row)
  const total = filteredRecords.length;
  const effectiveSize = printAll ? Math.max(total, 1) : pageSize;
  const pageCount = Math.max(1, Math.ceil(total / effectiveSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * effectiveSize;

  // Rows handed to <Table>: same columns for every row, hidden ones left out.
  // While printing, action-button columns are left out as well.
  const tableColumns = printAll ? printColumns : visibleColumns;

  const tableRows = useMemo(
    () =>
      filteredRecords
        .slice(pageStart, pageStart + effectiveSize)
        .map((rec) =>
          Object.fromEntries(
            tableColumns.map((c) => [c, rec.raw[c] === undefined ? '' : rec.raw[c]])
          )
        ),
    [filteredRecords, pageStart, effectiveSize, tableColumns]
  );

  // Charts drawn as images for printing (2-column grid). Built only while printing.
  const printCanvases = useMemo(
    () =>
      printAll && total > 0
        ? chartFields.map(({ field, chartType }) => ({
            field,
            canvas: renderChartCanvas(filteredPlain, field, chartType),
          }))
        : [],
    [printAll, total, chartFields, filteredPlain]
  );

  // Back to page 1 whenever the result set changes
  useEffect(() => {
    setPage(1);
  }, [activeTabs, search, sort, pageSize, records]);

  /* ------------------------------ actions ------------------------------ */

  const setTab = (field, value) =>
    setActiveTabs((prev) => ({ ...prev, [field]: value }));

  const clearFilters = () => {
    setActiveTabs({});
    setSearchInput('');
    setSearch('');
  };

  const applyPreset = (preset) => {
    const [from, to] = preset.range();
    setStartDate(from);
    setEndDate(to);
    handleSearch(from, to);
  };

  const toggleColumn = (col) =>
    setUserHidden((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });

  // paper: true is for PDF - it leaves out action-button columns like Print does
  const buildReport = ({ paper = false } = {}) => ({
    title: link?.page_title || 'Report',
    startDate,
    endDate,
    rows: filteredRecords.map((r) => r.raw),
    plainRows: filteredPlain,
    columns: paper ? printColumns : visibleColumns,
    filters: filterChips.map(({ label, value }) => ({ field: label, value })),
    cards: cardTotals,
    charts: chartFields,
  });

  // Short delay so the "Exporting…" state paints before heavy work starts
  const runExport = (label, task) => {
    setMenuOpen(false);
    setExporting(label);

    setTimeout(() => {
      try {
        task();
      } catch (err) {
        console.error(err);
        setError(`The ${label} export failed: ${err.message}`);
      } finally {
        setExporting('');
      }
    }, 30);
  };

  const hasData = total > 0;
  const hasCharts = chartFields.length > 0;

  /* ------------------------------- render ------------------------------- */

  return (
    <div className="py-3">
      <style>{`
.print-only { display: none; }
.print-charts { display: none; }

@media print {
  @page { size: A4 landscape; margin: 10mm; }

  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  body * { visibility: hidden !important; }

  #print-area,
  #print-area * { visibility: visible !important; }

  #print-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }

  #print-area .table-responsive { overflow: visible !important; }
  .print-only { display: block !important; }
  .print-hide { display: none !important; }

  /* table takes the full width; charts print as a 2-column grid above it */
  .print-full { flex: 0 0 100% !important; max-width: 100% !important; width: 100% !important; }

  .print-charts {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 12px;
    margin: 0 0 12px;
  }

  .print-chart {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .print-chart canvas {
    display: block;
    width: 100%;
    height: auto;
    max-height: 64mm;
    object-fit: contain;
  }
}
`}</style>

      {error && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-start print-hide"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Dismiss"
            onClick={() => setError('')}
          />
        </div>
      )}

      {/* ---------- Tab fields ---------- */}
      {tabFields.length > 0 && (
        <div className="print-hide">
          <div className="row g-2 mb-2">
            {tabFields.map((field) => (
              <div key={field} className="col-6 col-md-3">
                <button
                  type="button"
                  aria-pressed={activeField === field}
                  onClick={() => setActiveField(field)}
                  className={`btn w-100 ${
                    activeField === field ? 'btn-primary' : 'btn-outline-primary'
                  }`}
                >
                  {field}{' '}
                  <span className="badge bg-dark">
                    {distinctCounts[field] ?? 0}
                  </span>
                </button>
              </div>
            ))}
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            {tabOptions.map(({ value, count }) => {
              const selected = (activeTabs[activeField] ?? ALL) === value;

              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setTab(activeField, value)}
                  className={`btn btn-sm d-flex align-items-center gap-2 ${
                    selected ? 'btn-primary' : 'btn-outline-primary'
                  }`}
                >
                  <span>{value}</span>
                  <span className="badge bg-dark">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------- Active filters ---------- */}
      {filterChips.length > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3 print-hide">
          <small className="text-muted">Filtered by</small>
          {filterChips.map(({ key, label, value, remove }) => (
            <button
              key={key}
              type="button"
              className="btn btn-sm btn-light border"
              title={`Remove ${label} filter`}
              onClick={remove}
            >
              {label}: <strong>{value}</strong>{' '}
              <span aria-hidden="true">&times;</span>
            </button>
          ))}
          <button
            type="button"
            className="btn btn-sm btn-link"
            onClick={clearFilters}
          >
            Clear all
          </button>
        </div>
      )}

      {/* ---------- Date range ---------- */}
      <div className="card shadow-sm mb-3 print-hide">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label mb-1 text-muted small">
                Start date
              </label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label mb-1 text-muted small">
                End date
              </label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={() => handleSearch()}
                disabled={searching}
              >
                {searching && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  />
                )}
                {searching ? 'Searching' : 'Search'}
              </button>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2 mt-3">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={searching}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ================= Everything below is printed ================= */}
      <div id="print-area">
        <h2 className="mb-1">{link?.page_title}</h2>

        <p className="print-only text-muted small mb-3">
          Period: {startDate} to {endDate}
          {filterChips.length > 0 &&
            ` | Filtered by ${filterChips
              .map(({ label, value }) => `${label} = ${value}`)
              .join(', ')}`}
        </p>

        {/* ---------- Cards ---------- */}
        {cardTotals.length > 0 && (
          <div className="row g-3 mb-3 mt-1">
            {cardTotals.map(({ field, total: cardTotal }) => (
              <div className="col-6 col-md-3" key={field}>
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <small className="text-muted">{field}</small>
                    <h3 className="mb-0">{formatNumber(cardTotal)}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ---------- Charts for print: 2-column grid ---------- */}
        {printCanvases.length > 0 && (
          <div className="print-charts">
            {printCanvases.map(({ field, canvas }) => (
              <div className="print-chart" key={field}>
                <PrintChart canvas={canvas} />
              </div>
            ))}
          </div>
        )}

        {/* ---------- Table + charts ---------- */}
        <div className="row g-3">
          <div className={`${hasCharts ? 'col-lg-9' : 'col-12'} print-full`}>
            <div className="card shadow-sm">
              <div className="card-header d-flex flex-wrap align-items-center gap-2">
                <span className="text-muted small me-auto">
                  {total} of {records.length} rows
                </span>

                <div className="d-flex flex-wrap align-items-center gap-2 print-hide">
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    style={{ width: 190 }}
                    placeholder="Filter rows…"
                    aria-label="Filter rows"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />

                  <div className="input-group input-group-sm w-auto">
                    <select
                      className="form-select"
                      aria-label="Sort by"
                      value={sort.key}
                      onChange={(e) =>
                        setSort((prev) => ({ ...prev, key: e.target.value }))
                      }
                    >
                      <option value="">Sort by…</option>
                      {selectableColumns.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={!sort.key}
                      aria-label={
                        sort.dir === 'asc' ? 'Ascending' : 'Descending'
                      }
                      title={sort.dir === 'asc' ? 'Ascending' : 'Descending'}
                      onClick={() =>
                        setSort((prev) => ({
                          ...prev,
                          dir: prev.dir === 'asc' ? 'desc' : 'asc',
                        }))
                      }
                    >
                      {sort.dir === 'asc' ? '↑' : '↓'}
                    </button>
                  </div>

                  <div className="position-relative" ref={colsRef}>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary dropdown-toggle"
                      aria-haspopup="true"
                      aria-expanded={colsOpen}
                      disabled={selectableColumns.length === 0}
                      onClick={() => setColsOpen((open) => !open)}
                    >
                      Columns
                      {userHidden.size > 0 &&
                        ` (${visibleColumns.length}/${selectableColumns.length})`}
                    </button>

                    <div
                      className={`dropdown-menu dropdown-menu-end p-2 ${
                        colsOpen ? 'show' : ''
                      }`}
                      data-bs-popper="static"
                      style={{ minWidth: 230, maxHeight: 320, overflowY: 'auto' }}
                    >
                      <div className="d-flex justify-content-between align-items-center px-1 mb-1">
                        <span className="small fw-semibold">Show columns</span>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          disabled={userHidden.size === 0}
                          onClick={() => setUserHidden(new Set())}
                        >
                          Show all
                        </button>
                      </div>

                      {selectableColumns.map((col, idx) => {
                        const checked = !userHidden.has(col);

                        return (
                          <div className="form-check" key={col}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`col-toggle-${idx}`}
                              checked={checked}
                              disabled={checked && visibleColumns.length === 1}
                              onChange={() => toggleColumn(col)}
                            />
                            <label
                              className="form-check-label small"
                              htmlFor={`col-toggle-${idx}`}
                            >
                              {col}
                              {actionColumns.has(col) && (
                                <span className="text-muted"> (not printed)</span>
                              )}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={!hasData}
                    onClick={() => window.print()}
                  >
                    Print
                  </button>

                  <div className="position-relative" ref={menuRef}>
                    <button
                      type="button"
                      className="btn btn-sm btn-success dropdown-toggle"
                      aria-haspopup="true"
                      aria-expanded={menuOpen}
                      disabled={!hasData || Boolean(exporting)}
                      onClick={() => setMenuOpen((open) => !open)}
                    >
                      {exporting ? `Exporting ${exporting}…` : 'Export'}
                    </button>

                    <ul
                      className={`dropdown-menu dropdown-menu-end ${
                        menuOpen ? 'show' : ''
                      }`}
                      data-bs-popper="static"
                    >
                      <li>
                        <h6 className="dropdown-header">
                          Table ({total} rows, {visibleColumns.length} columns)
                        </h6>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() =>
                            runExport('Excel', () => exportExcel(buildReport()))
                          }
                        >
                          Excel (table, summary, chart data)
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() =>
                            runExport('CSV', () => exportCSV(buildReport()))
                          }
                        >
                          CSV (table only)
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() =>
                            runExport('PDF', () =>
                              exportPDF(buildReport({ paper: true }), { includeCharts: false })
                            )
                          }
                        >
                          PDF (table and totals)
                        </button>
                      </li>

                      {hasCharts && (
                        <>
                          <li>
                            <hr className="dropdown-divider" />
                          </li>
                          <li>
                            <h6 className="dropdown-header">Table and charts</h6>
                          </li>
                          <li>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                runExport('PDF', () =>
                                  exportPDF(buildReport({ paper: true }), {
                                    includeCharts: true,
                                  })
                                )
                              }
                            >
                              PDF (full report)
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                runExport('PNG', () =>
                                  exportChartsPNG(buildReport())
                                )
                              }
                            >
                              Charts as one PNG image
                            </button>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              <div
                className="card-body"
                style={{ opacity: searching ? 0.5 : 1 }}
              >
                {hasData ? (
                  <Table tbodyData={tableRows} />
                ) : (
                  <div className="text-center text-muted py-5">
                    {searching && 'Loading data…'}
                    {!searching && records.length === 0 && 'No data for this period.'}
                    {!searching && records.length > 0 && (
                      <>
                        No rows match the current filters.{' '}
                        <button
                          type="button"
                          className="btn btn-link p-0 align-baseline print-hide"
                          onClick={clearFilters}
                        >
                          Clear filters
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {hasData && (
                <div className="card-footer d-flex flex-wrap justify-content-between align-items-center gap-2 print-hide">
                  <small className="text-muted">
                    Showing {pageStart + 1}–
                    {Math.min(pageStart + effectiveSize, total)} of {total}
                  </small>

                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <select
                      className="form-select form-select-sm w-auto"
                      aria-label="Rows per page"
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                      {PAGE_SIZES.map((n) => (
                        <option key={n} value={n}>
                          {n} per page
                        </option>
                      ))}
                    </select>

                    <div className="btn-group btn-group-sm">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        aria-label="First page"
                        disabled={currentPage <= 1}
                        onClick={() => setPage(1)}
                      >
                        «
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        aria-label="Previous page"
                        disabled={currentPage <= 1}
                        onClick={() => setPage(currentPage - 1)}
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        aria-label="Next page"
                        disabled={currentPage >= pageCount}
                        onClick={() => setPage(currentPage + 1)}
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        aria-label="Last page"
                        disabled={currentPage >= pageCount}
                        onClick={() => setPage(pageCount)}
                      >
                        »
                      </button>
                    </div>

                    <small className="text-muted">
                      Page {currentPage} of {pageCount}
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>

          {hasCharts && (
            <div className="col-lg-3 d-flex flex-column gap-3 print-hide">
              {charts.map(({ field, chartType, cfg }) => (
                <div className="card shadow-sm" key={`${field}-${chartType}`}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0">{field}</h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary print-hide"
                        disabled={!hasData || Boolean(exporting)}
                        onClick={() =>
                          runExport('PNG', () =>
                            exportChartPNG(buildReport(), { field, chartType })
                          )
                        }
                      >
                        PNG
                      </button>
                    </div>

                    {hasData ? (
                      <Chart
                        type={cfg.type}
                        data={cfg.data}
                        options={cfg.options}
                      />
                    ) : (
                      <p className="text-muted small mb-0">No data to chart.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}